import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  ArrowLeft, 
  Users,
  Trophy,
  Gift,
  DollarSign,
  CheckCircle,
  Clock,
  History,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminReferrals() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [processingPrize, setProcessingPrize] = useState(false);
  const [prizeHistory, setPrizeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fetchReferrals = async () => {
    try {
      const result = await api.admin.getReferralRanking();
      setData(result);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast.error('Error al cargar referidos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrizeHistory = async () => {
    try {
      const result = await api.admin.getMonthlyPrizesHistory();
      setPrizeHistory(result.prizes || []);
    } catch (error) {
      console.error('Error fetching prize history:', error);
    }
  };

  useEffect(() => {
    fetchReferrals();
    fetchPrizeHistory();
  }, []);

  const handleProcessPrize = async () => {
    if (!window.confirm('¿Estás seguro de procesar el premio del mes anterior? Esta acción no se puede deshacer.')) {
      return;
    }

    setProcessingPrize(true);
    try {
      const result = await api.admin.processMonthlyReferralPrize();
      toast.success(result.message);
      if (result.winner) {
        toast.success(`Ganador: ${result.winner.name} con ${result.winner.active_referrals} referidos activos`);
      }
      fetchPrizeHistory();
    } catch (error) {
      console.error('Error processing prize:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el premio');
    } finally {
      setProcessingPrize(false);
    }
  };

  const handleMarkPaid = async (prizeId) => {
    try {
      await api.admin.markMonthlyPrizePaid(prizeId);
      toast.success('Premio marcado como pagado');
      fetchPrizeHistory();
    } catch (error) {
      toast.error('Error al marcar como pagado');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin">
          <Button variant="ghost" size="icon" className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <h1 className="heading-2">Referidos</h1>
      </div>

      {/* Prize Info */}
      <Card className="card-base bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="w-10 h-10 text-amber-500" />
              <div>
                <h3 className="font-semibold">Premio Mensual</h3>
                <p className="text-sm text-muted-foreground">{data?.month}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(data?.prize)}</p>
              <p className="text-xs text-muted-foreground">Para el #1 en referidos activos</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        <Button 
          onClick={handleProcessPrize}
          disabled={processingPrize}
          className="bg-amber-500 hover:bg-amber-600"
          data-testid="process-monthly-prize-btn"
        >
          {processingPrize ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              <Gift className="w-4 h-4 mr-2" />
              Procesar Premio del Mes Anterior
            </>
          )}
        </Button>

        <Button 
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
          data-testid="toggle-history-btn"
        >
          <History className="w-4 h-4 mr-2" />
          {showHistory ? 'Ocultar Historial' : 'Ver Historial'}
        </Button>
      </div>

      {/* Prize History */}
      {showHistory && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Premios Mensuales
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prizeHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No hay premios procesados aún
              </p>
            ) : (
              <div className="space-y-3">
                {prizeHistory.map((prize) => (
                  <div 
                    key={prize.id}
                    className={`p-4 rounded-xl border ${prize.no_winner ? 'bg-gray-50' : 'bg-green-50 border-green-200'}`}
                    data-testid={`prize-history-${prize.month_key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{prize.month_key}</p>
                        {prize.no_winner ? (
                          <p className="text-sm text-gray-500">Sin ganador (no hubo referidos activos)</p>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-green-700">{prize.winner_name}</p>
                            <p className="text-xs text-muted-foreground">{prize.winner_email}</p>
                            <p className="text-xs text-muted-foreground">
                              {prize.active_referrals} referidos activos
                            </p>
                          </>
                        )}
                      </div>
                      <div className="text-right">
                        {!prize.no_winner && (
                          <>
                            <p className="font-bold text-amber-600">{formatCurrency(prize.amount)}</p>
                            {prize.paid ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                <CheckCircle className="w-3 h-3" />
                                Pagado
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkPaid(prize.id)}
                                className="mt-1"
                              >
                                <DollarSign className="w-3 h-3 mr-1" />
                                Marcar Pagado
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Last Winner from previous month */}
      {data?.last_winner && !data.last_winner.no_winner && (
        <Card className="card-base bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <h3 className="font-semibold text-green-800">Último Ganador</h3>
                <p className="text-sm text-green-600">{data.last_winner.month_key}</p>
              </div>
            </div>
            <div className="bg-white/50 rounded-xl p-3">
              <p className="font-medium">{data.last_winner.winner_name}</p>
              <p className="text-sm text-muted-foreground">{data.last_winner.winner_email}</p>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm">
                  {data.last_winner.active_referrals} referidos activos
                </span>
                <span className="font-bold text-green-600">
                  {formatCurrency(data.last_winner.amount)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="card-base bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">¿Cómo funciona el premio mensual?</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Solo cuentan los <strong>referidos activos</strong> (que compraron tickets)</li>
                <li>El premio se procesa el primer día del mes siguiente</li>
                <li>El ganador es notificado por email y SMS</li>
                <li>El premio debe ser marcado como pagado manualmente</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Month Ranking */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-lg">Ranking del Mes Actual</CardTitle>
          <p className="text-sm text-muted-foreground">Solo referidos activos (que compraron)</p>
        </CardHeader>
        <CardContent>
          {(!data?.ranking || data.ranking.length === 0) ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="font-medium mb-2">No hay referidos activos este mes</h2>
              <p className="text-sm text-muted-foreground">
                El ranking se actualizará cuando haya referidos que compren tickets
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.ranking.map((entry) => (
                <div 
                  key={entry.user_id}
                  className={`p-4 rounded-xl border ${entry.position === 1 ? 'border-amber-300 bg-amber-50' : 'bg-white'}`}
                  data-testid={`referral-entry-${entry.position}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        entry.position === 1 ? 'bg-amber-400 text-amber-900' :
                        entry.position === 2 ? 'bg-slate-300 text-slate-700' :
                        entry.position === 3 ? 'bg-amber-700 text-amber-100' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {entry.position}
                      </div>
                      <div>
                        <p className="font-medium">{entry.name}</p>
                        <p className="text-sm text-muted-foreground">{entry.email}</p>
                        <p className="text-xs text-muted-foreground">{entry.phone}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{entry.monthly_referrals}</p>
                      <p className="text-xs text-muted-foreground">activos este mes</p>
                      <p className="text-xs text-muted-foreground">
                        {entry.active_referrals || 0} activos total
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
